import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { GetBotResponse, WhatsappContact } from 'src/app/models/bot.interface';
import { AttentionScheduleConfig, UpdateAttentionSchedulePayload } from 'src/app/models/attention-schedule.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { WhatsappBotService } from 'src/app/core/services/whatsapp-bot.service';
import { MessageService } from 'primeng/api';
import { finalize, Subject, takeUntil } from 'rxjs';

declare const FB: any;

interface WhatsappBenefitItem {
  icon: string;
  title: string;
  subtitle: string;
}

type WaEmbeddedAssets = { phone_number_id?: string; waba_id?: string };

function waEmbeddedSignupMessageListener(assets: WaEmbeddedAssets): (event: MessageEvent) => void {
  return (event: MessageEvent) => {
    if (!event.origin?.endsWith('facebook.com')) {
      return;
    }
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      const isStandardFlow = data?.type === 'WA_EMBEDDED_SIGNUP';
      const isCoexistenceFlow = data?.type === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';
      if (
        (isStandardFlow || isCoexistenceFlow) &&
        data?.data?.phone_number_id &&
        data?.data?.waba_id
      ) {
        assets.phone_number_id = String(data.data.phone_number_id);
        assets.waba_id = String(data.data.waba_id);
      }
    } catch {
      /* ignorar mensajes que no son JSON del flujo WA */
    }
  };
}

@Component({
  selector: 'globot-whatsapp-bot',
  templateUrl: './whatsapp-bot.component.html',
  styleUrls: ['./whatsapp-bot.component.scss'],
})
export class WhatsappBotComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() bot?: GetBotResponse;
  @Output() statusChange = new EventEmitter<{ status: string; style: { [key: string]: string } }>();

  whatsappBot: FormGroup;
  phoneNumber!: string;
  checked: boolean = false;
  is_enabled: boolean = false;
  callbackUrl = `${environment.apiAddress}/channels/whatsapp`;
  loading: boolean = false;
  deleteDialogVisible = false;
  embeddedConnected = false;
  embeddedSignupEnabled = false;
  onboardingStatus: string | null = null;
  facebookApiVersion = environment.facebookApiVersion;
  facebookAppId = environment.facebookAppId;
  /** Evita mostrar el formulario manual hasta conocer flags del backend (evita flash al refrescar). */
  waStateReady = false;

  attentionScheduleConfig: AttentionScheduleConfig | null = null;
  savingAttentionSchedule = false;

  readonly benefitItems: WhatsappBenefitItem[] = [
    {
      icon: 'pi pi-comments',
      title: 'Respuestas automáticas 24/7',
      subtitle: 'Atiende a tus clientes en cualquier momento',
    },
    {
      icon: 'pi pi-inbox',
      title: 'Gestión centralizada',
      subtitle: 'Todas tus conversaciones en un solo dashboard',
    },
    {
      icon: 'pi pi-shield',
      title: 'Integración certificada con WhatsApp',
      subtitle:
        'Integración con la plataforma de WhatsApp para empresas (Meta). Ideal si vas a usar un número dedicado al chatbot.',
    },
  ];

  recentContacts: WhatsappContact[] = [];
  totalContacts = 0;
  hasMoreContacts = false;
  loadingContacts = false;

  contactsDialogVisible = false;
  dialogContacts: WhatsappContact[] = [];
  dialogPage = 1;
  dialogPageSize = 10;
  dialogTotal = 0;
  dialogLoading = false;

  constructor(
    private fb: FormBuilder,
    private whatsappBotService: WhatsappBotService,
    private ms: MessageService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.whatsappBot = this.fb.group({
      whatsapp_token: [''],
      phoneNumber: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(12)]],
      graphUrl: [''],
      verify_token: [''],
    });
  }

  get showMetaConnect(): boolean {
    return (
      !!environment.whatsappConfigId &&
      this.embeddedSignupEnabled &&
      !this.embeddedConnected &&
      !this.phoneNumber
    );
  }

  get whatsappConnected(): boolean {
    return !!(this.phoneNumber || this.embeddedConnected);
  }

  get showManualEditableForm(): boolean {
    return !this.embeddedSignupEnabled && !this.embeddedConnected;
  }

  get isDisconnectedByMeta(): boolean {
    return this.onboardingStatus === 'disconnected';
  }

  get readonlyPhoneDisplay(): string {
    return this.phoneNumber || '—';
  }

  get dialogTotalPages(): number {
    return Math.max(1, Math.ceil(this.dialogTotal / this.dialogPageSize));
  }

  ngOnInit(): void {
    FB.init({
      appId: this.facebookAppId,
      cookie: true,
      xfbml: true,
      version: this.facebookApiVersion,
    });
    this.loadWhatsappState();
  }

  loadWhatsappState(): void {
    this.whatsappBotService
      .getBotWhatsapp(this.bot!.id)
      .pipe(
        finalize(() => {
          this.waStateReady = true;
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          const d = response.data;
          this.embeddedConnected = !!d.embedded_connected;
          this.embeddedSignupEnabled = !!d.whatsapp_embedded_signup_enabled;
          this.is_enabled = d.is_enabled;
          this.onboardingStatus = d.onboarding_status ?? null;

          if (this.showManualEditableForm) {
            this.whatsappBot
              .get('phoneNumber')!
              .setValidators([
                Validators.required,
                Validators.minLength(10),
                Validators.maxLength(12),
              ]);
            this.whatsappBot.get('whatsapp_token')!.clearValidators();
            this.whatsappBot.get('graphUrl')!.clearValidators();
          } else {
            this.whatsappBot.get('whatsapp_token')!.clearValidators();
            this.whatsappBot.get('graphUrl')!.clearValidators();
            this.whatsappBot.get('phoneNumber')!.clearValidators();
          }

          const rawToken = d.whatsapp_token ? String(d.whatsapp_token) : '';
          const displayedToken = rawToken.startsWith('Bearer ')
            ? rawToken.replace(/^Bearer\s+/i, '')
            : rawToken;
          this.whatsappBot.patchValue({
            whatsapp_token: displayedToken,
            phoneNumber: d.phone_number || '',
            graphUrl: d.whatsapp_endpoint || '',
            verify_token: d.verify_token != null ? String(d.verify_token) : '',
          });
          this.whatsappBot.updateValueAndValidity();
          this.phoneNumber = d.phone_number || '';
          this.attentionScheduleConfig = {
            attention_schedule_enabled: !!d.attention_schedule_enabled,
            attention_schedule_start_time: d.attention_schedule_start_time ?? null,
            attention_schedule_end_time: d.attention_schedule_end_time ?? null,
            away_message_enabled: !!d.away_message_enabled,
            away_message_text: d.away_message_text ?? null,
          };
          this.setStatus();
          this.changeDetectorRef.detectChanges();
          if (this.whatsappConnected) {
            this.loadRecentContacts();
          }
        },
        error: (err) => {
          console.error('Hubo un error al obtener los datos:', err);
          this.embeddedConnected = false;
          this.embeddedSignupEnabled = !!environment.whatsappConfigId;
          this.onboardingStatus = null;
          this.setStatus();
        },
      });
  }

  async connectWithMeta(): Promise<void> {
    if (!environment.whatsappConfigId || !this.bot?.id) {
      return;
    }
    this.loading = true;
    const embeddedAssets: WaEmbeddedAssets = {};
    const onMessage = waEmbeddedSignupMessageListener(embeddedAssets);
    window.addEventListener('message', onMessage);
    try {
      const code = await this.resolveEmbeddedSignupAuthCode();
      if (!code) {
        this.loading = false;
        return;
      }
      await this.waitForWaEmbeddedAssetIds(embeddedAssets, 45_000);
      const payload = this.buildEmbeddedSignupPayload(code, embeddedAssets);
      this.submitEmbeddedSignupCompletion(this.bot.id, payload);
    } finally {
      window.removeEventListener('message', onMessage);
    }
  }

  private async resolveEmbeddedSignupAuthCode(): Promise<string | null> {
    try {
      const response = await this.facebookLoginWhatsapp();
      const code = response?.authResponse?.code as string | undefined;
      if (!code) {
        this.showMessage(
          'error',
          'Acción fallida',
          'No se obtuvo el código de autorización de Meta.'
        );
        return null;
      }
      return code;
    } catch {
      this.showMessage('error', 'Acción fallida', 'No se pudo abrir el inicio de sesión con Meta.');
      return null;
    }
  }

  private async waitForWaEmbeddedAssetIds(
    assets: WaEmbeddedAssets,
    timeoutMs: number
  ): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline && (!assets.phone_number_id || !assets.waba_id)) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  private buildEmbeddedSignupPayload(
    code: string,
    assets: WaEmbeddedAssets
  ): { code: string; phone_number_id?: string; waba_id?: string } {
    const hasAssets = !!(assets.phone_number_id && assets.waba_id);
    if (!hasAssets) {
      this.showMessage(
        'warn',
        'Datos de Meta incompletos',
        'No se recibió el número desde la ventana de Meta. Se usará un modo de compatibilidad (menos fiable si hay varios números).'
      );
    }
    const payload: { code: string; phone_number_id?: string; waba_id?: string } = { code };
    if (hasAssets) {
      payload.phone_number_id = assets.phone_number_id;
      payload.waba_id = assets.waba_id;
    }
    return payload;
  }

  private embeddedSignupHttpErrorMessage(err: unknown): string {
    const e = err as { error?: { message?: string }; status?: number };
    return (
      e?.error?.message ??
      (e?.status === 409
        ? 'Elimina la configuración manual antes de conectar con Meta.'
        : 'No se pudo completar la conexión con Meta.')
    );
  }

  private submitEmbeddedSignupCompletion(
    botId: string,
    payload: { code: string; phone_number_id?: string; waba_id?: string }
  ): void {
    this.whatsappBotService
      .completeEmbeddedSignup(botId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.onboarding_status === 'coexistence_pending') {
            this.showMessage(
              'warn',
              'Paso adicional requerido',
              'Abre tu WhatsApp Business App y escanea el código QR que te llegó por mensaje para activar la conexión.'
            );
          } else {
            this.showMessage(
              'success',
              'Acción exitosa',
              res.message || 'WhatsApp conectado con Meta'
            );
          }
          this.loadWhatsappState();
          this.loading = false;
        },
        error: (err) => {
          this.showMessage('error', 'Error', this.embeddedSignupHttpErrorMessage(err));
          this.loading = false;
        },
      });
  }

  facebookLoginWhatsapp(): Promise<any> {
    return new Promise((resolve, reject) => {
      FB.login(
        (r: any) => {
          if (r) {
            resolve(r);
          } else {
            reject(new Error('Facebook login failed'));
          }
        },
        {
          config_id: environment.whatsappConfigId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            feature: 'whatsapp_embedded_signup',
            featureType: 'whatsapp_business_app_onboarding',
            sessionInfoVersion: '3',
          },
        }
      );
    });
  }

  onSubmit() {
    if (!this.showManualEditableForm) {
      return;
    }
    const phoneCtrl = this.whatsappBot.get('phoneNumber')!;
    phoneCtrl.markAsTouched();
    if (!phoneCtrl.valid) {
      this.showMessage('error', 'Acción fallida', 'Introduce un número de teléfono válido.');
      return;
    }

    const phoneNumber = phoneCtrl.value;

    if (this.bot?.id) {
      this.loading = true;
      this.whatsappBotService
        .getBotWhatsapp(this.bot.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const d = response.data;
            if (d?.phone_number || d?.embedded_connected) {
              this.updateWhatsappPhone(phoneNumber);
              this.phoneNumber = phoneNumber;
              this.setStatus();
            } else {
              this.postWhatsapp();
            }
            this.loading = false;
            this.changeDetectorRef.detectChanges();
          },
          error: (_err) => {
            this.postWhatsapp();
            this.loading = false;
          },
        });
    } else {
      this.showMessage('error', 'Acción fallida', 'El ID del servidor no es válido');
    }
  }

  updateWhatsappPhone(phoneNumber: string) {
    this.whatsappBotService
      .updateBotWhatsapp(this.bot!.id, phoneNumber, undefined!, undefined!, undefined!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (_response) => {
          this.showMessage('success', 'Acción exitosa', 'Tus datos se guardaron');
        },
        error: (err) => {
          if (err.status === 403) {
            this.showMessage(
              'error',
              'No disponible',
              err.error?.message ?? 'Usa Conectar con Meta para vincular WhatsApp.'
            );
          } else if (err.status === 409) {
            this.showMessage('error', 'Error al guardar', 'Teléfono ya asociado a otro chatbot.');
          } else {
            this.showMessage('error', 'Algo salió mal', 'Por favor, inténtalo de nuevo más tarde');
          }
        },
      });
  }

  postWhatsapp() {
    const phoneNumber = (this.whatsappBot.get('phoneNumber')!.value || '').trim();
    const whatsapp_token = (this.whatsappBot.get('whatsapp_token')!.value || '').trim();
    const graphUrl = (this.whatsappBot.get('graphUrl')!.value || '').trim();

    if (!phoneNumber) {
      this.showMessage('error', 'Acción fallida', 'Introduce un número de teléfono.');
      return;
    }
    if (!whatsapp_token || !graphUrl) {
      this.showMessage(
        'warn',
        'Configuración incompleta',
        'Para vincular WhatsApp por primera vez, usa el botón «Conectar WhatsApp con Meta».'
      );
      return;
    }

    let is_enabled = true;
    this.whatsappBotService
      .postBotWhatsapp(this.bot!.id, phoneNumber, whatsapp_token, graphUrl, is_enabled)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showMessage('success', 'Acción exitosa', '¡Bot de WhatsApp agregado con éxito!');
          this.whatsappBot.patchValue({
            verify_token: response.verify_token,
          });
          this.is_enabled = is_enabled;
          this.setStatus();
        },
        error: (err) => {
          if (err.status === 403) {
            this.showMessage(
              'error',
              'No disponible',
              err.error?.message ?? 'Usa Conectar con Meta para vincular WhatsApp.'
            );
          } else if (err.status === 409) {
            this.showMessage('error', 'Error al guardar', 'Teléfono ya asociado a otro chatbot.');
          } else {
            this.showMessage('error', 'Algo salió mal', 'Por favor, inténtalo de nuevo más tarde');
          }
        },
      });
  }
  showDeleteDialog(value: boolean): void {
    this.deleteDialogVisible = value;
  }

  closeDialogDelete(): void {
    this.deleteDialogVisible = false;
    this.loading = false;
  }

  deleteWhatsappConnection(): void {
    this.showDeleteDialog(false);
    if (!this.bot?.id) {
      return;
    }
    this.loading = true;
    this.whatsappBotService
      .deleteBotWhatsapp(this.bot.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showMessage('success', 'Acción exitosa', 'WhatsApp desvinculado.');
          this.loading = false;
          this.loadWhatsappState();
        },
        error: () => {
          this.loading = false;
          this.showMessage('error', 'Acción fallida', 'No se pudo desvincular WhatsApp.');
        },
      });
  }

  private todayString(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
  }

  private firstDayOfMonthString(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  }

  timeAgo(isoDate: string): string {
    const diff = Date.now() - new Date(isoDate).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'hace un momento';
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `hace ${days} día${days > 1 ? 's' : ''}`;
  }

  loadRecentContacts(): void {
    if (!this.bot?.id) return;
    this.loadingContacts = true;
    this.whatsappBotService
      .getRecentContacts(this.bot.id, this.firstDayOfMonthString(), this.todayString(), 1, 5)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.recentContacts = response.data ?? [];
          this.totalContacts = response.total ?? 0;
          this.hasMoreContacts = this.totalContacts > this.recentContacts.length;
          this.loadingContacts = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.recentContacts = [];
          this.totalContacts = 0;
          this.hasMoreContacts = false;
          this.loadingContacts = false;
        },
      });
  }

  openContactsDialog(): void {
    this.contactsDialogVisible = true;
    this.dialogPage = 1;
    this.loadContactsDialogPage(1);
  }

  closeContactsDialog(): void {
    this.contactsDialogVisible = false;
  }

  loadContactsDialogPage(page: number): void {
    if (!this.bot?.id) return;
    this.dialogLoading = true;
    this.dialogPage = page;
    this.whatsappBotService
      .getRecentContacts(
        this.bot.id,
        this.firstDayOfMonthString(),
        this.todayString(),
        page,
        this.dialogPageSize
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dialogContacts = response.data ?? [];
          this.dialogTotal = response.total ?? 0;
          this.dialogLoading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.dialogContacts = [];
          this.dialogLoading = false;
        },
      });
  }

  saveAttentionSchedule(payload: UpdateAttentionSchedulePayload): void {
    if (!this.bot?.id) {
      return;
    }
    const previousConfig = this.attentionScheduleConfig;
    this.savingAttentionSchedule = true;
    this.whatsappBotService
      .updateAttentionSchedule(this.bot.id, payload)
      .pipe(
        finalize(() => {
          this.savingAttentionSchedule = false;
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.attentionScheduleConfig = { ...this.attentionScheduleConfig, ...payload } as AttentionScheduleConfig;
          this.showMessage('success', 'Acción exitosa', 'Horario de atención actualizado');
        },
        error: (err) => {
          // Nueva referencia (mismos valores) para que el hijo revierta su estado local via ngOnChanges.
          this.attentionScheduleConfig = { ...previousConfig } as AttentionScheduleConfig;
          const detail = err?.error?.message || 'No se pudo guardar el horario de atención.';
          this.showMessage('error', 'Error al guardar', detail);
        },
      });
  }

  showMessage(severity: string, summary: string, detail: string) {
    this.ms.add({ key: 'tst', severity: severity, summary: summary, detail: detail });
  }

  switchChanged(event: any) {
    this.checked = event.checked;
    this.whatsappBotService
      .updateBotWhatsapp(this.bot!.id, undefined!, undefined!, undefined!, this.checked)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (_response) => {
          this.is_enabled = this.checked;
          this.setStatus();
          if (this.checked) {
            this.ms.add({
              key: 'tst',
              severity: 'success',
              summary: 'Acción exitosa',
              detail: '¡Bot de WhatsApp habilitado!',
            });
          } else {
            this.ms.add({
              key: 'tst',
              severity: 'success',
              summary: 'Acción exitosa',
              detail: '¡Bot de WhatsApp deshabilitado!',
            });
          }
        },
        error: (_err) => {
          this.ms.add({
            key: 'tst',
            severity: 'error',
            summary: 'Acción fallida',
            detail: 'Hubo un problema con el habilitado de tu bot de WhatsApp.',
          });
        },
      });
  }

  setStatus() {
    let status;
    if (!this.phoneNumber && !this.embeddedConnected) {
      status = {
        status: 'Sin configurar',
        style: {
          'padding-left': '10px',
          'padding-right': '10px',
          background: '#EDEDED',
          color: '#6B6B6B',
        },
      };
    } else if (this.isDisconnectedByMeta) {
      status = {
        status: 'Desconectado',
        style: {
          'padding-left': '10px',
          'padding-right': '10px',
          background: '#FECACA',
          color: '#DC2626',
        },
      };
    } else if (this.is_enabled) {
      status = {
        status: 'Activo',
        style: {
          'padding-left': '10px',
          'padding-right': '10px',
          background: '#BBF7D0',
          color: '#16A34A',
        },
      };
    } else {
      status = {
        status: 'Inactivo',
        style: {
          'padding-left': '10px',
          'padding-right': '10px',
          background: '#FFF9CE',
          color: '#E0A53E',
        },
      };
    }
    this.statusChange.emit(status);
  }

  trackByContactPhone(_index: number, contact: WhatsappContact): string {
    return contact.phone;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
